import { TableFilter } from "@neonumy/ant-design";
import { clientEntities } from "@neonumy/api-clients";
import { Space, Tag, Typography } from "antd";
import Text from "antd/es/typography/Text";
import { BsFillExclamationTriangleFill } from "react-icons/bs";
import { routes } from "config/routes";
import { DATE_FORMAT, DATE_FORMAT_API } from "config/settings";
import dayjs from "dayjs";
import { LIST_CLIENTS_PAGINATED_SELECT } from "queries/entities/clients";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { checkInvoiceType } from "utils/checkInvoiceType";
import useCoreStore from "views/hooks/useCoreStore";
import useDirectDebitApi from "views/Payments/hooks/useDirectDebitApi";

interface MissingInfoTablePropsTypes {
  deadlineDate?: dayjs.Dayjs;
}

export default function MissingInfoTable(props: MissingInfoTablePropsTypes): React.ReactElement {
  const { deadlineDate } = props;
  const { companyId }: RouteParams = useParams();
  const { t } = useTranslation();
  const companySettings = useCoreStore(state => state.companySettings);
  const decimalPlaces = companySettings?.decimalPlaces;

  const {
    listDirectDebitSaleDocuments,
    loadingListDirectDebitInvoices: loading,
    listInvoicesData: dataSource,
  } = useDirectDebitApi();

  useEffect(() => {
    if (deadlineDate) {
      listDirectDebitSaleDocuments({
        variables: {
          filter: {
            companyId,
            date: dayjs(deadlineDate).format(DATE_FORMAT_API),
            isValid: false,
          },
        },
      });
    }
  }, [deadlineDate]);

  const COLUMNS = [
    {
      title: t("Date"),
      dataIndex: "date",
      key: "date",
      filterType: {
        type: "DateFilter",
        maxDate: dayjs().endOf("day"),
        varName: ["date_Gte", "date_Lte"],
      },
      render: (value: string) => {
        if (value) {
          return (
            <Space size="small" direction="vertical">
              <Text>{dayjs(value).format(DATE_FORMAT)}</Text>
            </Space>
          );
        }
      },
    },
    {
      title: t("Deadline"),
      dataIndex: "paymentDeadlineDate",
      key: "paymentDeadlineDate",
      render: (value: string) => {
        if (value) {
          return (
            <Space size="small" direction="vertical">
              <Text>{dayjs(value).format(DATE_FORMAT)}</Text>
            </Space>
          );
        }
      },
    },
    {
      key: "reference",
      title: t("Reference"),
      dataIndex: "reference",
    },
    {
      title: t("Client"),
      dataIndex: "client",
      key: "client",
      sorter: true,
      filterType: {
        type: "AsyncSelectFilter",
        varName: "group_ClientId",
        asyncSelectSettings: {
          translations: {
            label: t("Select client"),
            placeholder: t("Select an option"),
            loadingMessage: t("Loading"),
            noOptionsMessage: t("No options"),
            button: {
              search: t("Search"),
              reset: t("Reset"),
            },
          },
          hasQueryFilter: true,
          queryOptions: {
            client: clientEntities,
            dataName: "listEntities",
            query: LIST_CLIENTS_PAGINATED_SELECT,
            variables: {
              filter: {
                companyId,
                entityType: "CLIENT",
                search: "",
              },
              limit: 20,
              orderBy: ["name"],
            },
          },
        },
      },
      render: (_text: unknown, record: ListDirectDebitInvoicesData) => {
        return <span>{`${record?.client?.name || ""}`}</span>;
      },
    },
    {
      key: "name",
      title: t("Name"),
      dataIndex: "name",
    },
    {
      key: "totalPrice",
      title: t("Total TTC"),
      dataIndex: "totalPrice",
      align: "right",
      render: (_text: unknown, record: ListDirectDebitInvoicesData) => {
        return <span>{`${Number(record?.totalPrice).toFixed(decimalPlaces)}€`}</span>;
      },
    },
    {
      key: "errors",
      title: t("Error"),
      dataIndex: "errors",
      render: (_text: unknown, record: ListDirectDebitInvoicesData) => {
        const errors = record?.errors?.map((item: string, idx: number) => {
          return (
            <Tag color={"#ff4d4f"} key={idx}>
              {String(item)}
            </Tag>
          );
        });

        return errors;
      },
    },
  ];

  const handleOnClick = (id: ID, type: string) => {
    const invoiceType = checkInvoiceType(type);
    routes.navigate(`/company/${companyId}/documents/${invoiceType}/edit/${id}`);
  };

  const hasMissingInfo = Number(dataSource?.length) > 0;

  if (!deadlineDate) {
    return <></>;
  }

  return (
    <>
      <Typography.Title
        level={5}
        className="debits-modal__section-title debits-modal__section-title--warning"
      >
        <span className="anticon">
          <BsFillExclamationTriangleFill />
        </span>
        <span>{t("Missing information")}</span>
        <span className="debits-modal__count">{Number(dataSource?.length) || 0}</span>
      </Typography.Title>
      <TableFilter
        className={`antd-newrai-table debits-modal__table debits-modal__table--secondary${
          hasMissingInfo ? "" : " debits-modal__table--empty"
        }`}
        loading={loading}
        onRow={record => {
          return {
            onClick: () => handleOnClick(record?.id, record?.type),
          };
        }}
        size="small"
        /* fixed budget: this table annotates the list, it never dominates it */
        scroll={hasMissingInfo ? { y: 160 } : undefined}
        columns={COLUMNS}
        dataSource={dataSource}
      />
    </>
  );
}
